<template>
  <div class="card">
    <a-page-header
      :title="$t('product.productDetails')"
      @back="() => $router.push('/marketPlace')"
    >
    </a-page-header>

    <div v-if="isLoading" class="product-details-container">
      <!-- Skeleton UI for loading state -->
      <div class="product-details">
        <div class="product-image-wrapper">
          <a-skeleton active />
        </div>

        <div class="product-info">
          <a-skeleton active :paragraph="{ rows: 8 }" />
        </div>
      </div>

      <!-- Skeleton for Description section -->
      <div class="product-description">
        <h3>
          <a-skeleton active :paragraph="{ rows: 1 }" :title="false" />
        </h3>
        <a-skeleton active :paragraph="{ rows: 4 }" />
      </div>

      <!-- Skeleton for Commands Table -->
      <h3>
        <a-skeleton active :paragraph="{ rows: 1 }" :title="false" />
      </h3>
      <a-skeleton active :paragraph="{ rows: 5 }" />
    </div>
    <div v-else class="product-details-container">
      <!-- Top section with image and basic info -->
      <div class="product-details">
        <div class="product-image-wrapper">
          <img
            :src="
              settings.resourcesUrl.replace('FILE_PATH', product.coverPicture)
            "
            alt="Product Image"
            class="product-image"
          />
        </div>

        <div class="product-info">
          <h3>{{ product.name }}</h3>
          <p>
            <strong>{{ $t("product.price") }}:</strong> {{ product.price }}
            {{ $t("product.currency") }}
          </p>
          <p class="availability">
            <strong>{{ $t("product.availability") }}:</strong>
            <a-tag :color="getTagColor1(product.availability)">
              {{ getTagText1(product.availability) }}
            </a-tag>
          </p>
          <p>
            <strong>{{ $t("product.quantity") }}:</strong>
            {{ product.quantity }}
          </p>
          <p>
            <strong>{{ $t("product.reference") }}:</strong>
            <a-tag class="ml-2">{{ product.reference }} </a-tag>
          </p>
          <p v-if="product.tags && product.tags.length > 0">
            <strong>{{ $t("product.tags") }}:</strong>
            <span class="ml-2">
              <a-tag
                v-for="tag in product.tags"
                :key="tag.name || tag"
                color="blue"
              >
                {{ tag.name || tag }}
              </a-tag>
            </span>
          </p>
          <p>
            <strong>{{ $t("command.createdAt") }}:</strong>
            {{ formattedDate(product.createdAt) }}
          </p>
          <p>
            <strong>{{ $t("product.lastUpdate") }}:</strong>
            {{ formattedDate(product.updatedAt) }}
          </p>
        </div>
      </div>

      <!-- Images Gallery Section -->
      <div
        v-if="product.pictures && product.pictures.length > 0"
        class="additional-images-section"
      >
        <h3 class="title">{{ $t("product.additionalPictures") }}</h3>
        <div style="margin-top: 20px">
          <a-carousel arrows dots-class="slick-dots slick-thumb">
            <a slot="customPaging" slot-scope="props">
              <img :src="getImgUrl(props.i)" class="thumbnail-image" />
            </a>
            <div
              v-for="(image, index) in product.pictures"
              :key="index"
              class="gallery-item"
            >
              <img
                :src="settings.resourcesUrl.replace('FILE_PATH', image)"
                class="gallery-image"
                @click="previewImage(image)"
              />
            </div>
          </a-carousel>
        </div>
      </div>

      <!-- Description section -->
      <div class="product-description">
        <h3 class="title">{{ $t("product.description") }}</h3>
        <div v-html="product.description"></div>
      </div>

      <!-- Commandes Section -->
      <h3 class="title">
        {{ $t("command.associatedCommands") }}
      </h3>
      <a-table
        :columns="commandColumns"
        :data-source="paginatedCommands"
        :pagination="paginationConfig"
        :rowKey="'_id'"
      >
        <template slot="client" slot-scope="text, record">
          {{ record.createdBy.userName }}
        </template>
        <template slot="etat" slot-scope="text, record">
          <a-tag :color="getTagColor(record.state)">
            {{ getStateLabel(record.state) }}
          </a-tag>
        </template>
        <template slot="date" slot-scope="text, record">
          {{ formattedDate(record.createdAt) }}
        </template>
        <!-- Action buttons (Edit, View, Delete) -->
        <span slot="operation" slot-scope="text, record">
          <span class="mr-2">
            <a-button @click="() => $router.push('/command/' + record._id)">
              <a-icon type="eye" /> {{ $t("action.voir") }}
            </a-button>
          </span>
          <span class="mr-2">
            <a-button type="primary" @click="showEditModal(record)">
              <a-icon type="edit" />{{ $t("action.modifier") }}
            </a-button>
          </span>
          <span>
            <a-popconfirm
              :title="$t('requis.supp')"
              @confirm="deleteCommand(record._id)"
            >
              <a-button type="danger">
                <a-icon type="delete" />
                <span style="margin-left: 8px">{{ $t("action.annuler") }}</span>
              </a-button>
            </a-popconfirm>
          </span>
        </span>

        <div
          slot="filterDropdown"
          slot-scope="{
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
            column,
          }"
          style="padding: 8px"
        >
          <a-input
            v-ant-ref="(c) => (searchInput = c)"
            :placeholder="`${$t('personnel.chercher')} ${column.title}`"
            :value="selectedKeys[0]"
            style="width: 188px; margin-bottom: 8px; display: block"
            @change="
              (e) => setSelectedKeys(e.target.value ? [e.target.value] : [])
            "
            @pressEnter="
              () => handleSearch(selectedKeys, confirm, column.dataIndex)
            "
          />
          <a-button
            type="primary"
            icon="search"
            size="small"
            class="search-button mr-2"
            @click="() => handleSearch(selectedKeys, confirm, column.dataIndex)"
            >{{ $t("paiement.chercher") }}</a-button
          >
          <a-button
            size="small"
            class="search-button"
            @click="() => handleReset(clearFilters, column.dataIndex)"
            >{{ $t("action.reinitialiser") }}</a-button
          >
        </div>
        <a-icon
          slot="filterIcon"
          slot-scope="filtered"
          type="search"
          :style="{ color: filtered ? '#108ee9' : undefined }"
        />
        <template slot="customRender" slot-scope="text, record, index, column">
          <!-- Check if there's a search term for this column -->
          <span v-if="searchStates[column.dataIndex]">
            <template v-if="column.dataIndex === 'createdBy'">
              <template
                v-for="(fragment, i) in record.createdBy.userName
                  .toString()
                  .split(
                    new RegExp(
                      `(?<=${searchStates[column.dataIndex]})|(?=${
                        searchStates[column.dataIndex]
                      })`,
                      'i'
                    )
                  )"
              >
                <mark
                  v-if="
                    fragment.toLowerCase() ===
                    searchStates[column.dataIndex].toLowerCase()
                  "
                  :key="i"
                  >{{ fragment }}</mark
                >
                <template v-else>{{ fragment }}</template>
              </template>
            </template>
            <template v-else>
              <template
                v-for="(fragment, i) in text
                  .toString()
                  .split(
                    new RegExp(
                      `(?<=${searchStates[column.dataIndex]})|(?=${
                        searchStates[column.dataIndex]
                      })`,
                      'i'
                    )
                  )"
              >
                <mark
                  v-if="
                    fragment.toLowerCase() ===
                    searchStates[column.dataIndex].toLowerCase()
                  "
                  :key="i"
                  >{{ fragment }}</mark
                >
                <template v-else>{{ fragment }}</template>
              </template>
            </template>
          </span>
          <!-- If there's no search for this column but there might be for other columns -->
          <span
            v-else-if="
              Object.keys(searchStates).some((key) => searchStates[key])
            "
          >
            <template v-if="column.dataIndex === 'createdBy'">
              <!-- Check if any other column's search term matches this text -->
              <template v-for="(searchValue, searchKey) in searchStates">
                <template
                  v-if="
                    searchValue &&
                    hasSearchMatch(record.createdBy.userName, searchValue)
                  "
                >
                  <template
                    v-for="(fragment, i) in record.createdBy.userName
                      .toString()
                      .split(
                        new RegExp(
                          `(?<=${searchValue})|(?=${searchValue})`,
                          'i'
                        )
                      )"
                  >
                    <mark
                      v-if="
                        fragment.toLowerCase() === searchValue.toLowerCase()
                      "
                      :key="i"
                      >{{ fragment }}</mark
                    >
                    <template v-else>{{ fragment }}</template>
                  </template>
                </template>
              </template>
              <!-- If no matches, just show the text -->
              <template
                v-if="
                  !Object.values(searchStates).some(
                    (val) =>
                      val && hasSearchMatch(record.createdBy.userName, val)
                  )
                "
              >
                {{ record.createdBy.userName }}
              </template>
            </template>
            <template v-else>
              <!-- Check if any other column's search term matches this text -->
              <template v-for="(searchValue, searchKey) in searchStates">
                <template
                  v-if="searchValue && hasSearchMatch(text, searchValue)"
                >
                  <template
                    v-for="(fragment, i) in text
                      .toString()
                      .split(
                        new RegExp(
                          `(?<=${searchValue})|(?=${searchValue})`,
                          'i'
                        )
                      )"
                  >
                    <mark
                      v-if="
                        fragment.toLowerCase() === searchValue.toLowerCase()
                      "
                      :key="i"
                      >{{ fragment }}</mark
                    >
                    <template v-else>{{ fragment }}</template>
                  </template>
                </template>
              </template>
              <!-- If no matches, just show the text -->
              <template
                v-if="
                  !Object.values(searchStates).some(
                    (val) => val && hasSearchMatch(text, val)
                  )
                "
              >
                {{ text }}
              </template>
            </template>
          </span>
          <!-- Default rendering when no searches are active -->
          <template v-else>
            <template v-if="column.dataIndex === 'createdBy'">{{
              record.createdBy.userName
            }}</template>
            <template v-else>{{ text }}</template>
          </template>
        </template>
      </a-table>
    </div>

    <!-- Edit Command Modal -->
    <edit-command
      :visible="isEditModalVisible"
      :command="selectedCommand"
      @cancel="isEditModalVisible = false"
    />
  </div>
</template>

<script>
import moment from "moment";
import apiClient from "@/services/axios";
import { mapState } from "vuex";
import EditCommand from "./EditCommand.vue";

export default {
  name: "ProductDetails",
  components: {
    EditCommand,
  },
  data() {
    return {
      product: {},
      associatedCommands: [],
      isLoading: false,
      isEditModalVisible: false,
      selectedCommand: null,
      currentPage: 1,
      pageSize: 5,
      searchText: "",
      searchedColumn: "",
      searchInput: null,
      searchStates: {},
    };
  },
  computed: {
    ...mapState(["settings"]),
    commandColumns() {
      return [
        {
          title: this.$t("product.reference"),
          dataIndex: "reference",
          key: "reference",
          sorter: (a, b) => a.reference.localeCompare(b.reference),
          scopedSlots: {
            filterDropdown: "filterDropdown",
            filterIcon: "filterIcon",
            customRender: "customRender",
          },
          onFilter: (value, record) =>
            record.reference.toLowerCase().includes(value.toLowerCase()),
          onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
              setTimeout(() => {
                this.searchInput.focus();
              });
            }
          },
        },
        {
          title: this.$t("command.clientName"),
          dataIndex: "createdBy",
          key: "createdBy",
          scopedSlots: {
            filterDropdown: "filterDropdown",
            filterIcon: "filterIcon",
            customRender: "customRender", // Change this from "client" to "customRender"
          },
          sorter: (a, b) =>
            a.createdBy.userName.localeCompare(b.createdBy.userName),
          onFilter: (value, record) =>
            record.createdBy.userName
              .toLowerCase()
              .includes(value.toLowerCase()),
          onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
              setTimeout(() => {
                this.searchInput.focus();
              });
            }
          },
        },
        {
          title: this.$t("command.date"),
          dataIndex: "createdAt",
          key: "createdAt",
          scopedSlots: { customRender: "date" },
          sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
        },
        {
          title: this.$t("command.state"),
          dataIndex: "state",
          key: "state",
          scopedSlots: { customRender: "etat" },
          sorter: (a, b) => a.state.localeCompare(b.state),
          filters: [
            { text: this.$t("command.pending"), value: "pending" },
            { text: this.$t("command.inProgress"), value: "inProgress" },
            { text: this.$t("command.validated"), value: "validated" },
            { text: this.$t("command.cancelled"), value: "canceled" },
          ],
          onFilter: (value, record) => record.state === value,
        },
        {
          title: this.$t("action.actions"),
          dataIndex: "operation",
          scopedSlots: { customRender: "operation" },
          fixed: "right",
        },
      ];
    },
    paginatedCommands() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.associatedCommands.slice(start, end);
    },
    paginationConfig() {
      return {
        current: this.currentPage,
        pageSize: this.pageSize,
        total: this.associatedCommands.length,
        onChange: this.handlePageChange,
      };
    },
  },
  created() {
    const productId = this.$route.params.id;
    this.fetchProductDetails(productId);
  },
  methods: {
    async fetchProductDetails(productId) {
      this.isLoading = true;
      try {
        const productResponse = await apiClient.get(
          `/marketplace/product/${productId}`
        );
        this.product = productResponse.data.product;
        this.associatedCommands = productResponse.data.relatedCommands.map(
          (command) => {
            if (command.state === "in progress") {
              command.state = "inProgress";
            }
            return command;
          }
        );
      } catch (error) {
        this.$message.error(this.$t("errors.fetchDataFailed"));
      } finally {
        this.isLoading = false;
      }
    },
    formattedDate(date) {
      return date ? moment(date).format("DD/MM/YYYY HH:mm") : "";
    },
    handlePageChange(page) {
      this.currentPage = page;
    },
    getTagText1(availability) {
      switch (availability) {
        case "available":
          return this.$t("product.available");
        case "soon":
          return this.$t("product.comingSoon");
        case "out":
          return this.$t("product.outOfStock");
        default:
          return this.$t("product.unavailable");
      }
    },
    getTagColor1(availability) {
      switch (availability) {
        case "available":
          return "green";
        case "soon":
          return "orange";
        case "out":
          return "red";
        default:
          return "gray";
      }
    },
    getTagColor(state) {
      switch (state) {
        case "pending":
          return "orange";
        case "inProgress":
          return "blue";
        case "validated":
          return "green";
        case "cancelled":
          return "red";
        default:
          return "gray";
      }
    },
    getStateLabel(state) {
      switch (state) {
        case "pending":
          return this.$t("command.pending");
        case "inProgress":
          return this.$t("command.inProgress");
        case "validated":
          return this.$t("command.validated");
        case "cancelled":
          return this.$t("command.cancelled");
      }
    },
    showEditModal(record) {
      this.selectedCommand = record;
      this.isEditModalVisible = true;
    },
    deleteCommand(commandId) {
      this.isLoading = true;
      apiClient
        .delete(`/marketplace/command/${commandId}`)
        .then(() => {
          this.$message.success(this.$t("success.commandSupp"));
          this.fetchProductDetails(this.product._id);
        })
        .catch(() => {
          this.$message.error(this.$t("error.autorisationSupp"));
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    getImgUrl(i) {
      return this.settings.resourcesUrl.replace(
        "FILE_PATH",
        this.product.pictures[i]
      );
    },

    previewImage(image) {
      // Open image in a new tab
      window.open(
        this.settings.resourcesUrl.replace("FILE_PATH", image),
        "_blank"
      );
    },

    handleSearch(selectedKeys, confirm, dataIndex) {
      confirm();
      // Store search state for this specific column
      this.searchStates[dataIndex] = selectedKeys[0] || "";

      // Keep these for backward compatibility
      this.searchText = selectedKeys[0];
      this.searchedColumn = dataIndex;
    },

    handleReset(clearFilters, dataIndex) {
      clearFilters();
      // Clear the search state for this column
      this.searchStates[dataIndex] = "";

      // If this was the current searched column, clear those too
      if (this.searchedColumn === dataIndex) {
        this.searchText = "";
        this.searchedColumn = "";
      }
    },

    // Add a new helper method to check if text matches search
    hasSearchMatch(text, searchText) {
      if (!text || !searchText) return false;
      return text.toString().toLowerCase().includes(searchText.toLowerCase());
    },
  },
};
</script>

<style scoped>
.product-details-container {
  display: flex;
  flex-direction: column;
  gap: 30px;
  margin: 10px 40px;
}

.product-details {
  display: flex;
  align-items: flex-start;
  gap: 30px;
}

.product-image-wrapper {
  flex-shrink: 0;
}

.product-image {
  max-width: 250px;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.product-info {
  flex-grow: 1;
  padding: 10px 0;
}

.product-info h3 {
  margin-bottom: 10px;
  font-weight: bold;
}

.product-description {
  margin: 10px 0;
}

.product-description h3 {
  margin-bottom: 10px;
}

.title {
  position: relative;
  padding-bottom: 10px;
  margin-bottom: 15px;
}

.title::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: 0;
  height: 2px;
  width: 50px;
  background-color: #1890ff;
}

.availability {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Gallery styles */
.additional-images-section {
  margin: 20px 0;
}

.ant-carousel >>> .slick-dots {
  height: auto;
  position: relative;
  bottom: 0;
  margin: 10px 0;
}

.ant-carousel >>> .slick-slide img {
  border: 5px solid #fff;
  display: block;
  margin: auto;
  max-width: 80%;
}

.ant-carousel >>> .slick-thumb {
  bottom: 0;
  padding: 0;
}

.ant-carousel >>> .slick-thumb li {
  width: 80px;
  height: 70px;
  margin: 0 4px;
}

.ant-carousel >>> .slick-thumb li img {
  width: 100%;
  height: 100%;
  filter: grayscale(100%);
  border-radius: 4px;
}

.ant-carousel >>> .slick-thumb li.slick-active img {
  filter: grayscale(0%);
}

.gallery-item {
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 20px;
}

.gallery-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
  cursor: pointer;
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

.search-button {
  width: 90px;
  height: 30px;
}

.skeleton-gallery {
  display: flex;
  gap: 20px;
  margin-top: 20px;
  justify-content: center;
}

.loading-spinner {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
}
</style>
